import { canDockAt, stationDefinition } from "@frontier/content/stations";
import {
  hasIncompleteTutorial,
  isActiveContract,
} from "@frontier/content/contract-orders";
import type { ContractTerms } from "@frontier/content/contract-orders";
import { FACTIONS } from "@frontier/content/factions";
import { REGIONS } from "@frontier/content/world-layout";
import { contractComplete } from "@frontier/content/progression";
import type {
  MissionView,
  Profile,
  ShipSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { currentContractDescription } from "./contract-description";
import { galaxyDestination, galaxyRegionAt } from "./galaxy-navigation";
import type { GalaxyTarget } from "./galaxy-navigation";

interface Props {
  world: WorldSnapshot;
  pilot: ShipSnapshot;
  profile: Profile;
  missions: readonly MissionView[];
  connected: boolean;
  selected: GalaxyTarget;
  onNavigate: (target: GalaxyTarget) => void;
  onJoinEvent: (id: string) => void;
  interactive?: boolean;
  eventId?: string;
  onEventChange?: (id: string) => void;
}
interface GalaxyContract {
  orderId: string;
  sectorId: string;
  terms: ContractTerms;
  progress: number | null;
  goal: number;
  objective: string;
  ready: boolean;
}
export function galaxyMapContracts(
  profile: Profile,
  missions: readonly MissionView[],
): GalaxyContract[] {
  if (profile.docked)
    return profile.selectedContracts.flatMap((selection) => {
      const offer = profile.contractBoard.offers.find(
        (item) =>
          item.orderId === selection.orderId &&
          item.contractId === selection.contractId,
      );
      if (!offer) return [];
      const terms = offer[selection.difficulty];
      return [
        {
          orderId: selection.orderId,
          sectorId: terms.sectorId,
          terms,
          progress: null,
          goal: terms.goal,
          objective: currentContractDescription(terms.description),
          ready: false,
        },
      ];
    });
  return missions
    .filter(
      (mission) => mission.runId === profile.runId && isActiveContract(mission),
    )
    .map((mission) => ({
      orderId: mission.orderId,
      sectorId: mission.sectorId,
      terms: mission.terms,
      progress: mission.progress,
      goal: mission.goal,
      objective: mission.objective.name,
      ready: contractComplete(mission),
    }));
}
function regionDescription(id: string): string {
  return (
    REGIONS.find((region) => region.id === id)?.description ??
    "Условия области не сообщены."
  );
}

export function GalaxyMapDetails({
  world,
  pilot,
  profile,
  missions,
  connected,
  selected,
  onNavigate,
  onJoinEvent,
  interactive = true,
  eventId,
  onEventChange,
}: Props) {
  const station = selected.kind === "station";
  const base =
    selected.kind === "station"
      ? stationDefinition(selected.id ?? "meridian")
      : null;
  const accessible = !base || canDockAt(base, pilot.factionId);
  const region = station
    ? world.regions.find((item) => item.id === base?.sectorId)
    : world.regions.find((item) => item.id === selected.id);
  const definition = REGIONS.find((item) => item.id === region?.id);
  const current = galaxyRegionAt(world, pilot);
  const route = galaxyDestination(world, pilot, selected);
  const docked = pilot.docked || profile.docked;
  const intro = hasIncompleteTutorial(profile);
  const canNavigate =
    connected &&
    !docked &&
    !pilot.dead &&
    !pilot.pending &&
    accessible &&
    route !== null;
  const ownMissions = galaxyMapContracts(profile, missions).filter(
    (contract) => station || contract.sectorId === region?.id,
  );
  const events = world.publicEvents.filter(
    (event) =>
      event.sectorId === region?.id &&
      (event.status === "active" || event.status === "available"),
  );
  const eventsAllowed = !intro;
  const routeGate = route?.gateId
    ? world.gates.find((gate) => gate.id === route.gateId)
    : undefined;
  const routeLabel = route?.sectorIds
    .map((id) => world.regions.find((item) => item.id === id)?.name ?? id)
    .join(" → ");
  const buttonLabel =
    docked && station && current?.id === base?.sectorId
      ? "ТЫ НА СТАНЦИИ"
      : !station && current?.id === region?.id
        ? "ТЫ В ЭТОЙ ОБЛАСТИ"
        : station
          ? "К СТАНЦИИ"
          : "К ВРАТАМ";
  const event = events.find((item) => item.id === eventId) ?? events[0];
  return (
    <div className="galaxy-map-details">
      <header className="galaxy-detail-heading">
        <span className="galaxy-eyebrow">
          {station
            ? "СТАНЦИЯ"
            : definition?.factionId
              ? FACTIONS[definition.factionId].name
              : "ОБЩАЯ ОБЛАСТЬ"}
        </span>
        <h2>{station ? base?.name : (region?.name ?? "Область недоступна")}</h2>
      </header>
      <p
        className="galaxy-place-description"
        title={region ? regionDescription(region.id) : undefined}
      >
        {station
          ? "Сдача груза · контракты · ангар"
          : region
            ? (definition?.resourceSummary ?? regionDescription(region.id))
            : ""}
      </p>
      {!accessible && (
        <p className="galaxy-place-description">
          Док этой фракции недоступен. Используй свою или нейтральную станцию.
        </p>
      )}
      {ownMissions.length > 0 && (
        <section
          className="galaxy-tooltip-missions"
          aria-label="Твои принятые задания"
        >
          {ownMissions.map((mission) => (
            <article className="galaxy-contract" key={mission.orderId}>
              <b title={currentContractDescription(mission.terms.description)}>{mission.terms.title}</b>
              <small
                title={mission.ready ? "Готов к сдаче" : mission.objective}
              >
                {mission.progress === null
                  ? "ПРИНЯТ НА СТАНЦИИ"
                  : `${mission.progress}/${mission.goal}`}{" "}
                · {mission.ready ? "Готов к сдаче" : "В работе"}
              </small>
            </article>
          ))}
        </section>
      )}
      {event && (
        <section
          className="galaxy-tooltip-events"
          aria-label="События выбранной области"
        >
          {interactive && events.length > 1 ? (
            <select
              aria-label="Публичное событие"
              data-galaxy-focus
              value={event.id}
              onChange={(change) => onEventChange?.(change.currentTarget.value)}
            >
              {events.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.terms.title}
                </option>
              ))}
            </select>
          ) : (
            <b className="galaxy-event-title">
              {interactive ? event.terms.title : `События: ${events.length}`}
            </b>
          )}
          {interactive && (
            <>
              <small className="galaxy-event-reward">
                {event.terms.credits} КР · {event.terms.components} комп. ·{" "}
                {event.participants} участвуют
              </small>
              <button
                type="button"
                data-galaxy-focus
                disabled={
                  !connected ||
                  !eventsAllowed ||
                  pilot.dead ||
                  (event.type === "relay" && !profile.factionId)
                }
                onClick={() => {
                  if (
                    connected &&
                    eventsAllowed &&
                    !pilot.dead &&
                    (event.type !== "relay" || profile.factionId)
                  )
                    onJoinEvent(event.id);
                }}
              >
                {docked ? "ОТСТЫКОВАТЬСЯ К СОБЫТИЮ" : "ОТСЛЕЖИВАТЬ СОБЫТИЕ"}
              </button>
              {!eventsAllowed && (
                <small className="galaxy-event-lock">
                  Доступно после обучения.
                </small>
              )}
              {event.type === "relay" && !profile.factionId && (
                <small className="galaxy-event-lock">
                  Для захвата нужны регистрация и выбор фракции.
                </small>
              )}
            </>
          )}
        </section>
      )}
      {interactive ? (
        <div className="galaxy-route-action">
          <button
            type="button"
            className="galaxy-navigate"
            data-galaxy-focus
            disabled={!canNavigate}
            title={
              route
                ? `${route.gateId ? `Через врата: ${routeGate?.name ?? route.label}` : "Курс к доку станции"}. ${routeLabel ?? ""}. Переход через нужные врата — автоматически при подлёте.`
                : undefined
            }
            onClick={() => {
              if (canNavigate && route) onNavigate(selected);
            }}
          >
            {buttonLabel}
            <span aria-hidden="true">↗</span>
          </button>
          {!canNavigate && (
            <small className="galaxy-route-hint">
              {!connected
                ? "Нет связи с сектором."
                : pilot.dead
                  ? "Дождись восстановления корабля."
                  : pilot.pending
                    ? "Дождись завершения текущего действия."
                    : docked
                      ? "Для курса сначала отстыкуйся."
                      : !route && current?.id !== region?.id
                        ? "Нет доступного пути через известные врата."
                        : route?.gateId
                          ? "Долети до врат и подтверди переход. Автоматического прыжка нет."
                          : "Карта задаёт курс. Стыковка — отдельным действием."}
            </small>
          )}
        </div>
      ) : null}
    </div>
  );
}
